import Bugsnag from '@bugsnag/js'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { toast } from 'react-toastify'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Progress,
  Container,
  Table,
  Alert
} from 'reactstrap'
import { compose } from 'redux'
import {
  dispatchSweetAlert,
  clearSweetAlert,
  fetchUserCredit,
  fetchUser,
  checkCartStockErrors
} from '../../actions/index'
import branditRails from '../../api/brandit_rails'
import { IS_PRODUCTION } from '../../constants'
import { addTaxablePriceToCartItems, addPromoData } from '../../helpers/cartItemsFormatter'
import { clearCartItems } from '../../redux/storeConfig/slices/slice.cart'
import {
  setPaymentStatusAction,
  setUsedAction
} from '../../redux/storeConfig/slices/slice.checkoutSessionToken/checkoutSessionToken.actions'
import { standardizeAddress } from '../../util/utils'
import dollar_price from '../general/DollarPrice'
import { setOrderProcessing } from './checkoutFunctions'
import { useShipperHQCheck } from './hooks/useShipperHQCheck'
import AuthorizeNetCheckoutUIModal from './payments/AuthorizeNetCheckoutUIModal'
import StripeCheckoutUIModal from './payments/StripeCheckoutUIModal'
import { buildSelectedRatesArray } from './ShippingMethodSelector/shippingGroupsHelpers'
import { validateMinimumCreditCardTotal, getMinimumCreditCardValidationMessage } from './utils'

const CheckoutConfirm = props => {
  const [loading, setLoading] = useState(false)

  const { isShipEngine } = useShipperHQCheck(props.integrations)
  const [addressesStandardized, setAddressesStandardized] = useState({ shipping: {}, billing: {} })
  const [transactionId, setTransactionId] = useState('')
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)

  const [orderError, setOrderError] = useState({
    step: '', // one of: token, payment, fallback, order, orderItems
    message: '',
    endpoint: '',
    apiResponse: {},
    control: ''
  })
  const [orderInProgress, setOrderInProgress] = useState(false)
  const [stockCheckLoading, setStockCheckLoading] = useState(false)
  const [orderBackupId, setOrderBackupId] = useState(null)
  // Remove isModalOpen state since we'll use props.show

  // Update toggleModal function to use props.show
  const toggleModal = () => {
    props.hideModal()
  }

  // when order progress is finished fetch credits
  useEffect(() => {
    if (orderInProgress === false && props.currentUser.id) {
      props.fetchUserCredit()
    }
  }, [orderInProgress, props.currentUser.id])

  // handle errors for each step in order process
  useEffect(() => {
    const errorObject = {}
    if (orderError.step) {
      switch (orderError.step) {
        case 'order':
          errorObject.message = 'Order creation failure'
          break

        default:
          break
      }
      dispatchErrorAlert(
        orderError,
        IS_PRODUCTION, // notifyBugsnag
        props.portal
      )
    }
  }, [orderError.step])

  // Prevent refresh on order confirmation
  useEffect(() => {
    if (orderInProgress) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [orderInProgress])

  const handleOrderSuccess = order => {
    // Update checkout session token state to reflect successful order
    props.setPaymentStatus('captured')
    props.setUsed(true)

    props.clearCartItems()
    if (!props.currentUser.id) {
      setOrderInProgress(false)
      // TODO: show order invoice for completed order
      // handle post-logout for guest user (clear cart)
      setTimeout(() => {
        setOrderInProgress(false)
        props.hideModal() // Close the modal before redirecting
        redirectToMyOrders(order)
      }, 100)
      setTimeout(() => {
        toast.success('Order placed successfully!')
      }, 100)
    } else {
      props.fetchUser(props.portal.default_location).then(() => {
        // Allow progress bar to reach 100% before redirect
        setTimeout(() => {
          setOrderInProgress(false)
          props.hideModal() // Close the modal before redirecting
          redirectToMyOrders(order)
        }, 100)
      })
    }
  }

  useEffect(() => {
    setAddressesStandardized({
      shipping: standardizeAddress(props.selectedCustomerAddress),
      billing: standardizeAddress(props.selectedBillingAddress)
    })
  }, [props.selectedCustomerAddress, props.selectedBillingAddress])

  const dispatchErrorAlert = (error = {}, notifyBugsnag = false, portal) => {
    if (notifyBugsnag === true) {
      Bugsnag.addMetadata('apiResponse', error)
      Bugsnag.addMetadata('endpoint', error.endpoint)
      Bugsnag.notify(new Error(error.message ? error.message : ''))
    }

    props.dispatchSweetAlert({
      customButtons: errorMessage(portal, error),
      closeOnClickOutside: false
    })
  }

  // Error message component

  // SN_CHECK REMOVE FALBACK ID AND MAKE CONTROL WORK
  const errorMessage = (portal, error) => {
    const payments = error.apiResponse.payments
    const errorCode = error?.apiResponse?.control && error.apiResponse.control.replace('_', '')
    const referenceNum = error?.apiResponse?.log_tracker
    const message = error?.apiResponse?.user_message
    const authnetError = error?.apiResponse?.authnet_response
    const transactionResponse = error?.apiResponse?.transaction_response

    return (
      <div>
        <h3 className="order-error-h3">Order Processing Error</h3>
        <Card>
          <CardBody style={{ paddingTop: '6px', paddingBottom: '6px!important' }}>
            <br />
            <div className="mb-2">
              Error code:{errorCode ? ` ${errorCode} | ` : ' ERR-001'}
              {referenceNum ? `Ref #: ${referenceNum}` : null}
            </div>
            <Alert color="danger">
              {error.apiResponse.user_message !== 0 ? (
                <>
                  {transactionResponse && (
                    <div>
                      <b>{transactionResponse}</b>
                    </div>
                  )}
                  <div>
                    <b>{message || 'Unable to process order.'}</b>
                  </div>
                </>
              ) : null}
            </Alert>
            {authnetError?.messages?.map((error, index) => (
              <>
                <div className="mb-2">AuthNet Error code: {error.code}</div>
                <Alert key={index} color="danger">
                  <b>{error.text}</b>
                </Alert>
              </>
            ))}
            <div className="order-error-contact-info">
              {portal.packing_slip_support_email_address} | {portal.packing_slip_support_phone_number}
              <br />
            </div>
          </CardBody>
        </Card>
        <br />
        <Card style={{ backgroundColor: '#E5E4E2', color: '363636' }}>
          <CardBody style={{ paddingTop: '6px', paddingBottom: '1px' }}>
            {payments && payments.total_value == 0 ? (
              <div style={{ paddingTop: '2px', paddingBottom: '2px' }}>
                Your card or any credits you may have used <br />
                <strong>have not been charged</strong>.
              </div>
            ) : (
              <div>
                <div style={{ paddingTop: '2px', paddingBottom: '2px' }}>
                  These transactions <b>have been processed</b>:
                </div>
                <br />
                <Table>
                  <thead>
                    <tr>
                      <th className="fw-right">Payment Method</th>
                      <th className="fw-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments && payments.account_credits_value > 0 ? (
                      <tr>
                        <td className="fw-right">{localStorage.getItem('account_credit_def')}</td>
                        <td className="fw-right">{dollar_price(payments.account_credits_value, 2, null, false)}</td>
                      </tr>
                    ) : null}
                    {payments && payments.coop_value > 0 ? (
                      <tr>
                        <td className="fw-right">{localStorage.getItem('coop_def')}</td>
                        <td className="fw-right">{dollar_price(payments.coop_value, 2, null, false)}</td>
                      </tr>
                    ) : null}
                    {payments && payments.credit_card_value > 0 && payments.net_30_value == 0 ? (
                      <tr>
                        <td className="fw-right">Credit Card</td>
                        <td className="fw-right">{dollar_price(payments.credit_card_value, 2, null, false)}</td>
                      </tr>
                    ) : null}
                    {payments && payments.net_30_value > 0 ? (
                      <tr>
                        <td className="fw-right">Net 30</td>
                        <td className="fw-right">${payments.net_30_value}</td>
                      </tr>
                    ) : null}
                    {payments && payments.gift_card_value > 0 ? (
                      <tr>
                        <td className="fw-right">Gift Card</td>
                        <td className="fw-right">{dollar_price(payments.gift_card_value, 2, null, false)}</td>
                      </tr>
                    ) : null}
                    {payments && payments.points_value > 0 ? (
                      <tr>
                        <td className="fw-right">Points</td>
                        <td className="fw-right">{payments.points_value * props.currentLocation.conversion_ratio}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td className="fw-right">
                        <b>Total</b>
                      </td>
                      <td className="fw-right">
                        <b>
                          {payments && payments.points_value
                            ? payments.points_value * props.currentLocation.conversion_ratio
                            : dollar_price(payments && payments.total_value, 2, null, false)}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <br />
        <>
          <Button className="mf-primary-btn" style={{ marginRight: '10px' }} onClick={handleViewMyAccount} size="lg">
            View My Account
          </Button>
          {error && error.apiResponse && error.apiResponse.control === '_ERR1' ? (
            <Button className="mf-outline-btn" onClick={returnToStore} size="lg">
              Return to Store
            </Button>
          ) : (
            <Button className="mf-outline-btn" onClick={handleTryAgain} size="lg">
              Try Again
            </Button>
          )}
        </>
      </div>
    )
  }

  const handleViewMyAccount = () => {
    props.clearSweetAlert()
    props.history.push('/customer/account/dashboard')
  }

  const returnToStore = () => {
    props.clearSweetAlert()
    props.hideModal()
    props.history.push('./shop')
  }

  const handleTryAgain = () => {
    props.clearSweetAlert()
    props.hideModal()
    props.history.go(0)
  }

  const handleConfirmErrorModal = () => {
    props.clearSweetAlert()
    props.hideModal()
    initializeOrderProgress()
  }

  const redirectToMyOrders = order => {
    if (props.currentUser.id) {
      props.history.push('/customer/account/dashboard')
    } else {
      // ✅ FIX: Access order properties directly, not nested
      const orderNumber = order?.order_number
      const orderId = order?.id
      props.history.push(`/order-details/${orderNumber}/${orderId}`)
    }
  }

  // Prevent page refresh on order confirmation
  const handleBeforeUnload = event => {
    event.preventDefault()
    event.returnValue = ''
  }

  // ** Place Order **
  const createOrder = () => {
    const formData = new FormData()
    const checkout = props.checkout
    // Payment
    const { selectedCustomerAddress, selectedBillingAddress, youPay, paymentMethod } = checkout
    const { card_number, card_expiry, card_cvc } = checkout.ccData

    const coopDeduction = checkout.creditsUsed.coopCredits
    const creditDeduction = checkout.creditsUsed.accountCredits
    const giftDeduction = checkout.creditsUsed.giftCard
    const lmsPointsDeduction = checkout.creditsUsed.lmsPoints

    const appliedPromotions = props.appliedPromotions.map(addPromoTaxablePrice)

    formData.append('payment_coop', coopDeduction)
    formData.append('payment_credit_note', creditDeduction)
    formData.append('payment_gift', giftDeduction)
    formData.append('payment_lms_points', lmsPointsDeduction)

    const trimmedCardNumber = card_number.replace(/\s/g, '')

    formData.append('payment_method', paymentMethod)
    formData.append('total_cents', youPay)

    formData.append('card_number', trimmedCardNumber)
    formData.append('card_expire_date', card_expiry)
    formData.append('card_security_code', card_cvc)
    formData.append('card_number_two_first_digits', trimmedCardNumber.substr(0, 1))
    formData.append('card_last_four_digit', trimmedCardNumber.substr(-4))
    formData.append('payment_amount', parseFloat(youPay / 100).toFixed(2))

    // send back encrypted checkout data
    formData.append('checkout_data', JSON.stringify(checkout.checkoutData))

    // Price totals
    formData.append('tax', checkout.taxQuote)
    formData.append('applied_promotions', JSON.stringify(appliedPromotions)) //todo: just send promo id?
    formData.append('shipment_in_cents', checkout.shippingQuote)
    formData.append('items_discount_in_cents', checkout.subtotalReduction + checkout.productReduction)
    formData.append('shipping_discount_in_cents', checkout.shippingReduction)
    formData.append('coop_discount_in_cents', coopDeduction)
    formData.append('subtotal_in_cents', checkout.subtotal)
    formData.append('net_30_confirmed', checkout.net30Confirmed)

    // Cart info
    formData.append('user_cart_id', props.checkout.userCartId)
    if (!props.currentUser.id) {
      // guest user
      formData.append('guest_user_id', localStorage.getItem('guest_user_id'))
      formData.append('contact_email', checkout.contactEmail)
    }

    const cartItemsWithPromoData = addPromoData(props.cartItems)
    const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(cartItemsWithPromoData)
    formData.append('cart_json', JSON.stringify(cartItemsWithTaxablePrice))

    // Order info
    if (transactionId) {
      formData.append('transaction_id', transactionId === -1 ? null : transactionId)
    }
    formData.append('po_number', checkout.poNumber)
    formData.append('checkout_session_token', props.checkoutSessionToken)
    formData.append('customer_notes', checkout.customerNotes)
    if (props.checkoutFormData && props.checkoutFormData.length > 0) {
      formData.append('checkout_form', JSON.stringify(props.checkoutFormData))
    }

    // Shipping info
    formData.append('billing_address', JSON.stringify(selectedBillingAddress))
    formData.append('customer_address', JSON.stringify(selectedCustomerAddress))
    if (props.portal.multiple_address_shipping && props.currentUser.id) {
      formData.append('multi_address_selection', JSON.stringify(props.checkout.multiAddressSelection))
    }

    // if portal does not have managed addresses enabled OR
    // if it DOES but there are no doors_hubs
    if (
      !props.portal.has_address_managed ||
      (props.portal.has_address_managed && !props.managed_customer_addresses.is_system_managed) // TODO: UPdate this
    ) {
      // if selected customer address is present
      if (!isEmpty(props.selectedCustomerAddress)) {
        formData.append('customer_address_id', props.selectedCustomerAddress.id)
      }
      // if selected Customer address is not present but not required
      else if (
        !props.selectedCustomerAddress.id &&
        props.cartItems.every(ci => ci.shippable_type === 'Digital Delivery')
      ) {
        // formData.append('customer_address_id', null);
      }
      // if selected customer address is not present but required - error
      else {
        const alertMessage = props.currentUser.id
          ? 'Please select a shipping address.'
          : 'Please enter a shipping address.'
        props.dispatchSweetAlert({
          type: 'warning',
          onConfirm: handleConfirmErrorModal,
          showCancel: false,
          alertMessage
        })
        return
      }

      !props.checkout.youPay === 0 && formData.append('billing_address_id', props.selectedBillingAddress.id)
    }

    // if smart delivery address, append appropriate keys for address & phone
    if (!isEmpty(props.selectedCustomerAddress) && !!props.selectedCustomerAddress.m_able) {
      formData.append('customer_address', JSON.stringify(props.selectedCustomerAddress))
      formData.append('delivery_phone_number', props.selectedCustomerAddress.phone)
    } else {
      // else use phone from address
      formData.append('delivery_phone_number', props.selectedCustomerAddress.address_phone)
    }

    // Third party billing - send billing data if selected
    if (props.checkoutRates?.shippingPaymentMethod === 'third_party_billing') {
      formData.append('third_party_billing_data', JSON.stringify(props.checkoutRates.thirdPartyBillingData))
      formData.append('shipping_payment_method', 'third_party_billing')
    } else if (isShipEngine && props.checkoutRates?.selectedRatesByGroup) {
      // Only include selected_shipping_rates for ShipEngine orders.
      // For ShipperHQ orders, encrypted shipping is already in checkout_data.shipping.
      const selectedRates = buildSelectedRatesArray(props.checkoutRates.selectedRatesByGroup)
      formData.append('selected_shipping_rates', JSON.stringify(selectedRates))
      formData.append('shipping_rate_log_id', props.checkoutRates.shippingRateLogId)
    }

    branditRails
      .post('/orders/validate', formData)
      .then(response => {
        if (response && response.data && response.data.success === true) {
          const orderBackupId = response.data.order_backup_id || null
          if (props.checkout.paymentMethod === 'credit_card' && props.checkout.youPay > 0) {
            setShowPaymentDialog(true)
          } else {
            const orderFormData = new FormData()
            const orderData = extractOrderDataForPayment()

            // Add the backup ID to the existing order data
            orderFormData.append('order_backup_id', orderBackupId)

            // Add all the other order data with proper stringification
            Object.keys(orderData).forEach(key => {
              if (orderData[key] !== null && orderData[key] !== undefined) {
                if (typeof orderData[key] === 'object') {
                  orderFormData.append(key, JSON.stringify(orderData[key]))
                } else {
                  orderFormData.append(key, orderData[key])
                }
              }
            })

            // Create the order
            branditRails
              .post('/orders/create', orderFormData)
              .then(response => {
                if (response && response.data && response.data.status === 'failed') {
                  setOrderInProgress(false)
                  setLoading(false)
                  setOrderError({
                    step: 'order',
                    message: 'Order creation failed',
                    endpoint: '/orders/create',
                    apiResponse: response.data,
                    control: response.data.control
                  })
                } else {
                  handleOrderSuccess(response.data.order || {})
                }
              })
              .catch(error => {
                // Check if it's an AxiosError with response data
                if (error.response && error.response.data) {
                  const responseData = error.response.data
                  setOrderError({
                    step: 'order',
                    message: 'Order creation failed',
                    endpoint: '/orders/create',
                    apiResponse: responseData,
                    control: responseData.control
                  })
                } else {
                  setOrderError({
                    step: 'order',
                    message:
                      props.checkout.youPay === 0
                        ? 'Zero amount order creation failure'
                        : 'Net 30 order creation failure',
                    endpoint: '/orders/create',
                    apiResponse: error,
                    control: null
                  })
                }
                setOrderInProgress(false)
                setLoading(false)
              })
              .finally(() => {
                setLoading(false)
              })
          }
        } else if (response && response.data && response.data?.status === 'failed') {
          setOrderInProgress(false)
          // setLoading(false)
          setOrderError({
            step: 'order',
            message: 'Order validation failed',
            endpoint: `/orders/create`,
            apiResponse: response.data,
            control: response.data.control
          })
        } else {
          setOrderInProgress(false)
          setLoading(false)
        }
      })
      .catch(error => {
        setOrderError({
          step: 'order',
          message: 'Order validation request failed',
          endpoint: `/orders/create`,
          formData,
          apiResponse: error, // NOTE: this is the error object, not necessarily from API
          control: null
        })
        setOrderInProgress(false)
        setLoading(false)
      })
  }

  // Check Stock, Then Create Order
  const handleOrderConfirm = () => {
    setLoading(true)

    // Check minimum credit card validation before proceeding
    if (validateMinimumCreditCardTotal(props.checkout.youPay, props.checkout.paymentMethod)) {
      const validationMessage = getMinimumCreditCardValidationMessage()
      props.dispatchSweetAlert({
        type: 'warning',
        alertMessage: `${validationMessage.title}\n\n${validationMessage.message}`,
        showCancel: false,
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn mr-0'
      })
      setLoading(false)
      return
    }

    setOrderInProgress(true)
    setStockCheckLoading(true)
    props.checkCartStockErrors(props.checkout.userCartId).then(resp => {
      setStockCheckLoading(false)
      if (resp && resp.cart_items && resp.cart_items.length > 0) {
        setOrderInProgress(false)
        props.hideModal()
      } else {
        createOrder()
      }
    })
  }

  const addPromoTaxablePrice = promotion => ({
    ...promotion,
    updated_cart: promotion.updated_cart.map(cartItem => ({
      ...cartItem,
      taxable_price: cartItem.promo_data.discounted_price
    }))
  })

  // ** END ORDER FLOW

  // OPTION A: Extract order data for payment modal
  const extractOrderDataForPayment = () => {
    const appliedPromotions = props.appliedPromotions.map(addPromoTaxablePrice)
    const cartItemsWithPromoData = addPromoData(props.cartItems)
    const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(cartItemsWithPromoData)

    // Shipping flow is derived from integrations via useShipperHQCheck.

    const orderData = {
      // Payment info
      payment_method: props.checkout.paymentMethod,
      total_cents: props.checkout.youPay,
      payment_amount: parseFloat(props.checkout.youPay / 100).toFixed(2),

      // Checkout data
      checkout_data: props.checkout.checkoutData,

      // Price totals
      tax: props.checkout.taxQuote,
      applied_promotions: appliedPromotions,
      shipment_in_cents: props.checkout.shippingQuote,
      items_discount_in_cents: props.checkout.subtotalReduction + props.checkout.productReduction,
      shipping_discount_in_cents: props.checkout.shippingReduction,
      coop_discount_in_cents: props.checkout.coopDeduction || 0,
      subtotal_in_cents: props.checkout.subtotal,
      net_30_confirmed: props.checkout.net30Confirmed,

      // Cart info
      user_cart_id: props.checkout.userCartId,
      cart_json: cartItemsWithTaxablePrice,

      // Order info
      po_number: props.checkout.poNumber,
      checkout_session_token: props.checkoutSessionToken,
      customer_notes: props.checkout.customerNotes,
      checkout_form: props.checkoutFormData,

      // Address info
      billing_address: props.selectedBillingAddress,
      customer_address: props.selectedCustomerAddress,
      multi_address_selection:
        props.portal.multiple_address_shipping && props.currentUser.id ? props.checkout.multiAddressSelection : null,

      // User info
      guest_user_id: !props.currentUser.id ? localStorage.getItem('guest_user_id') : null,
      contact_email: !props.currentUser.id ? props.checkout.contactEmail : null,

      // Payment-related fields -
      payment_coop: props.checkout.creditsUsed?.coopCredits || 0,
      payment_credit_note: props.checkout.creditsUsed?.accountCredits || 0,
      payment_gift: props.checkout.creditsUsed?.giftCard || 0,
      payment_lms_points: props.checkout.creditsUsed?.lmsPoints || 0,

      // Order backup ID from validation response - FIXING THIS FIELD
      order_backup_id: orderBackupId || null
    }

    // Third party billing - send billing data if selected
    if (props.checkoutRates?.shippingPaymentMethod === 'third_party_billing') {
      orderData.third_party_billing_data = props.checkoutRates.thirdPartyBillingData
      orderData.shipping_payment_method = 'third_party_billing'
    } else if (isShipEngine && props.checkoutRates?.selectedRatesByGroup) {
      // Only include selected_shipping_rates for ShipEngine orders.
      // For ShipperHQ orders, encrypted shipping is already in checkout_data.shipping.
      orderData.selected_shipping_rates = buildSelectedRatesArray(props.checkoutRates.selectedRatesByGroup)
      orderData.shipping_rate_log_id = props.checkoutRates.shippingRateLogId
    }

    return orderData
  }

  const initializeOrderProgress = () => {
    setOrderInProgress(false)
    setTransactionId('')
    setLoading(false)
    setOrderError({})
    props.setOrderProcessing(false)
  }

  useEffect(() => {
    setAddressesStandardized({
      shipping: standardizeAddress(props.selectedCustomerAddress),
      billing: standardizeAddress(props.selectedBillingAddress)
    })
  }, [props.selectedCustomerAddress, props.selectedBillingAddress])

  const resolvePaymentMessage = () => {
    if (props.portal.use_lms_points) {
      return <span>Total Points to be Used: {dollar_price(props.checkout.creditsUsed.lmsPoints)}</span>
    }
    switch (props.checkout.paymentMethod) {
      case 'credit_card':
        return <span>Amount to be Charged to Credit Card: {dollar_price(props.checkout.youPay)}</span>
      case 'net30':
        return (
          <span>
            Payment Amount Due ({props.netTerm || 'Net 30'}): {dollar_price(props.checkout.youPay)}
          </span>
        )
      default:
        return <span>Amount to be Charged to Credit Card: {dollar_price(props.checkout.youPay)}</span>
    }
  }

  const paymentMethod = props.checkout.paymentMethod === 'credit_card' ? 'Credit Card' : 'Net30'
  const hidePhysicalShipping = props.cartItems.every(ci => ci.shippable_type === 'Digital Delivery')
  const showDigitalDeliveryMessage = props.cartItems.some(ci => ci.shippable_type === 'Digital Delivery')

  return (
    <>
      {/* Replace DevExtreme Popup with Reactstrap Modal */}
      <Modal isOpen={props.show} toggle={toggleModal} size="lg" centered backdrop="static">
        <ModalHeader>{props.paymentMethod === 'credit_card' ? 'Order Summary' : 'Order Confirmation'}</ModalHeader>
        <ModalBody>
          <Container className="order-confirmation-container">
            <Row>
              <Col>
                <p className="mt-3" style={{ fontSize: '1.2rem' }}>
                  {props.checkout.paymentMethod === 'credit_card' && props.checkout.youPay > 0 ? (
                    <>
                      Please confirm your order details below.
                      <br />
                      On the next step, you'll securely enter your payment information.
                      <br />
                      Your order will only be placed once payment is successfully processed.
                    </>
                  ) : (
                    'Please confirm your order. Upon final confirmation, your order will be placed.'
                  )}
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                {showDigitalDeliveryMessage && (
                  <p>
                    Some or all items in your cart will be delivered digitally to the email{' '}
                    <strong>{props.currentUser.email}</strong>.
                  </p>
                )}

                {hidePhysicalShipping ? null : (
                  <p>
                    <strong>Ship To:</strong> &nbsp;
                    {addressesStandardized.shipping.address_line_1}, &nbsp;
                    {addressesStandardized.shipping.address_line_2
                      ? `${addressesStandardized.shipping.address_line_2}, `
                      : ''}
                    {addressesStandardized.shipping.city}, &nbsp;
                    {addressesStandardized.shipping.state}, &nbsp;
                    {addressesStandardized.shipping.zip_code}
                  </p>
                )}

                {!props.checkout.youPay === 0 ? (
                  <span>
                    <p>
                      Billing Address: &nbsp;
                      {addressesStandardized.billing.address_line_1}, &nbsp;
                      {addressesStandardized.billing.address_line_2
                        ? `${addressesStandardized.billing.address_line_2}, `
                        : ''}
                      {addressesStandardized.billing.city}, &nbsp;
                      {addressesStandardized.billing.state}, &nbsp;
                      {addressesStandardized.billing.zip_code}
                    </p>
                    <p>Payment Method: {paymentMethod}</p>
                  </span>
                ) : null}

                {props.checkout.creditsUsed.coopCredits ? (
                  <div>
                    {localStorage.getItem('coop_def')}:&nbsp; -{dollar_price(props.checkout.creditsUsed.coopCredits)}
                  </div>
                ) : null}

                {props.checkout.creditsUsed.accountCredits ? (
                  <div>
                    {localStorage.getItem('account_credit_def')} Payment:&nbsp; -
                    {dollar_price(props.checkout.creditsUsed.accountCredits)}
                  </div>
                ) : null}

                {props.checkout.creditsUsed.giftCard ? (
                  <div>
                    <div>Gift Card Credit Payment:&nbsp; -{dollar_price(props.checkout.creditsUsed.giftCard)}</div>
                  </div>
                ) : null}

                {props.currentLocation.show_price ? <p className="payment-total">{resolvePaymentMessage()}</p> : null}
              </Col>
            </Row>
            <Row>
              <Col>
                {orderInProgress && (
                  <div className="mt-4" style={{ paddingTop: '88px' }}>
                    <Progress animated value={stockCheckLoading ? 25 : 50}>
                      {stockCheckLoading ? 'Checking Stock' : 'Creating Order'}
                    </Progress>
                    <p className="mt-3 mb-0">
                      <strong>Order in progress. Please do not navigate away from page. </strong>
                    </p>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </ModalBody>
        <ModalFooter>
          <Button className="mf-outline-btn" onClick={toggleModal} disabled={loading}>
            Back to Checkout
          </Button>
          <Button className="mf-primary-btn" disabled={loading || !props.canProceed} onClick={handleOrderConfirm}>
            {props.checkout.paymentMethod === 'credit_card' && props.checkout.youPay > 0
              ? 'Proceed to Secure Payment'
              : 'Confirm Order'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Gateway Dialogs - Show as overlay */}
      {showPaymentDialog && props.paymentGatewayConfig.isStripe && props.checkout.youPay > 0 && (
        <StripeCheckoutUIModal
          renderFormOnly={true}
          hideModal={() => {
            setShowPaymentDialog(false)
            initializeOrderProgress() // ✅ This resets all order states
            toggleModal()
          }}
          paymentGatewayConfig={props.paymentGatewayConfig}
          orderData={extractOrderDataForPayment()}
          onPaymentSuccess={result => {
            setShowPaymentDialog(false)
            handleOrderSuccess(result.order?.order || {}) // ✅ Pass the nested order object
          }}
          onPaymentError={error => {
            setShowPaymentDialog(false)
            setOrderInProgress(false)
            setLoading(false)
          }}
        />
      )}

      {showPaymentDialog && props.paymentGatewayConfig.isAuthorizeNet && props.checkout.youPay > 0 && (
        <AuthorizeNetCheckoutUIModal
          amount={props.checkout.youPay}
          currentUser={props.currentUser}
          paymentGatewayConfig={props.paymentGatewayConfig}
          orderData={extractOrderDataForPayment()}
          renderFormOnly={true}
          hideModal={() => {
            setShowPaymentDialog(false)
            initializeOrderProgress() // ✅ This resets all order states
            toggleModal()
          }}
          onPaymentSuccess={result => {
            setShowPaymentDialog(false)
            handleOrderSuccess(result.order?.order || {}) // ✅ Pass the nested order object
          }}
          onPaymentError={error => {
            setShowPaymentDialog(false)
            setOrderInProgress(false)
            setLoading(false)
          }}
        />
      )}
    </>
  )
}

CheckoutConfirm.propTypes = {
  show: PropTypes.bool.isRequired,
  hideModal: PropTypes.func.isRequired,
  canProceed: PropTypes.bool.isRequired,
  selectedBillingAddress: PropTypes.object.isRequired,
  selectedCustomerAddress: PropTypes.object.isRequired,
  checkout: PropTypes.shape({
    userCartId: PropTypes.number.isRequired,
    selectedCustomerAddress: PropTypes.object.isRequired,
    selectedBillingAddress: PropTypes.object.isRequired,
    youPay: PropTypes.number.isRequired,
    paymentMethod: PropTypes.string.isRequired,
    ccData: PropTypes.shape({
      card_number: PropTypes.string.isRequired,
      card_expiry: PropTypes.string.isRequired,
      card_cvc: PropTypes.string.isRequired
    }).isRequired,
    creditsUsed: PropTypes.shape({
      coopCredits: PropTypes.number,
      accountCredits: PropTypes.number,
      giftCard: PropTypes.number,
      lmsPoints: PropTypes.number
    }).isRequired,
    checkoutData: PropTypes.object.isRequired,
    taxQuote: PropTypes.number.isRequired,
    shippingQuote: PropTypes.number.isRequired,
    subtotalReduction: PropTypes.number.isRequired,
    productReduction: PropTypes.number.isRequired,
    shippingReduction: PropTypes.number.isRequired,
    net30Confirmed: PropTypes.bool.isRequired,
    poNumber: PropTypes.string,
    customerNotes: PropTypes.string,
    multiAddressSelection: PropTypes.array.isRequired
  }).isRequired,
  portal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    has_address_managed: PropTypes.bool.isRequired,
    has_address_standard: PropTypes.bool.isRequired,
    use_lms_points: PropTypes.bool.isRequired,
    digital_proofing_text_1: PropTypes.string,
    digital_proofing_text_2: PropTypes.string,
    packing_slip_support_email_address: PropTypes.string,
    packing_slip_support_phone_number: PropTypes.string,
    context_dictionary: PropTypes.shape({
      net_term_def: PropTypes.string
    }).isRequired
  }).isRequired,
  checkoutSessionToken: PropTypes.string.isRequired,
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  tax: PropTypes.object.isRequired,
  appliedPromotions: PropTypes.arrayOf(PropTypes.object).isRequired,
  userCredits: PropTypes.object.isRequired,
  managed_customer_addresses: PropTypes.object.isRequired,
  currentLocation: PropTypes.shape({
    show_price: PropTypes.bool.isRequired,
    conversion_ratio: PropTypes.number.isRequired
  }).isRequired,
  currentUser: PropTypes.object.isRequired,
  checkoutFormData: PropTypes.arrayOf(PropTypes.object).isRequired,
  netTerm: PropTypes.string,
  paymentGatewayType: PropTypes.string.isRequired,
  paymentGatewayConfig: PropTypes.shape({
    type: PropTypes.string.isRequired,
    isStripe: PropTypes.bool.isRequired,
    isAuthorizeNet: PropTypes.bool.isRequired,
    config: PropTypes.object,
    clientKey: PropTypes.string,
    apiLoginID: PropTypes.string,
    publishableKey: PropTypes.string,
    environment: PropTypes.string
  }).isRequired,
  dispatchSweetAlert: PropTypes.func.isRequired,
  clearSweetAlert: PropTypes.func.isRequired,
  fetchUserCredit: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  setOrderProcessing: PropTypes.func.isRequired,
  checkCartStockErrors: PropTypes.func.isRequired
}

const mapStateToProps = state => {
  return {
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    selectedCustomerAddress: state.checkout.selectedCustomerAddress,
    checkout: state.checkout,
    portal: state.portal,
    checkoutSessionToken: state.checkoutSessionToken.token,
    cartItems: state.cartItems,
    tax: state.tax,
    appliedPromotions: state.checkout.appliedPromotions,
    userCredits: state.userCredits,
    managed_customer_addresses: state.managed_customer_addresses,
    currentLocation: state.storeViews.current,
    currentUser: state.currentUser,
    checkoutFormData: state.checkout.checkoutFormData,
    netTerm: state.portal.context_dictionary.net_term_def,
    checkoutRates: state.checkoutRates,
    integrations: state.integrations
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, {
    dispatchSweetAlert,
    clearSweetAlert,
    fetchUserCredit,
    fetchUser,
    setOrderProcessing,
    checkCartStockErrors,
    clearCartItems,
    setPaymentStatus: setPaymentStatusAction,
    setUsed: setUsedAction
  })
)(CheckoutConfirm)
